import { useDebouncedCallback } from '@tanstack/react-pacer';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppTopbar } from '@/components/app-topbar';
import { PageLayout } from '@/components/page-layout';
import { testDeterminationsQuery } from '@/modules/germination-test/hooks/germination-queries';
import { type GerminationTestKind } from '@/modules/germination-test/services/germination-types';
import {
  GERMINATION_TEST_ROUTES,
  toGerminationTestKind,
} from '@/modules/germination-test/utils/test-kind';
import { DeadlineToggle } from '@/modules/installation/components/deadline-toggle';
import { InstallationFilters } from '@/modules/installation/components/installation-filters';
import { InstallationList } from '@/modules/installation/components/installation-list';
import { INSTALLATION_DETERMINATIONS } from '@/modules/installation/domain/installation-constants';
import {
  type InstallationDeadline,
  type InstallationListItem,
} from '@/modules/installation/services/installation-types';
import {
  parseDeterminationFilters,
  parseDeterminationIds,
  serializeDeterminationFilters,
} from '@/modules/installation/utils/determination-filters';
import { AnalysisFilters } from '@/modules/lab-shared/components/analysis-filters';
import { AnalysisPanel, type SortOption } from '@/modules/lab-shared/components/analysis-panel';
import { AnalysisTabs } from '@/modules/lab-shared/components/analysis-tabs';
import { type PeriodValue } from '@/modules/lab-shared/components/period-filter';
import { ProducerSelect } from '@/modules/lab-shared/components/producer-select';
import { useAnalysisSync } from '@/modules/lab-shared/hooks/use-analysis-sync';
import { ANALYSIS_COLUMN_KEYS } from '@/modules/lab-shared/lib/analysis-column-keys';
import {
  type AnalysisListSearch,
  analysisListSearchSchema,
  type AnalysisTab,
  clearedFilters,
  type DeadlineFilter,
  hasActiveFilters,
  resolveSort,
  toDetailSearch,
} from '@/modules/lab-shared/lib/list-search';
import {
  type AnalysisStatusFilter,
  parseStatusFilters,
  serializeStatusFilters,
} from '@/modules/lab-shared/services/analysis-schemas';
import { SandBedList } from '@/modules/sand-bed/components/sand-bed-list';
import { TetrazoliumList } from '@/modules/tetrazolium-test/components/tetrazolium-list';

const SEARCH_DEBOUNCE_MS = 400;

const INSTALLATION_DEADLINES: Record<DeadlineFilter, InstallationDeadline> = {
  hoje: 'today',
  atrasadas: 'overdue',
};

const TAB_TITLE_KEYS: Record<AnalysisTab, string> = {
  instalacao: 'lab.tabs.installation',
  tetrazolio: 'lab.tabs.tetrazolium',
  'canteiro-areia': 'lab.tabs.sandBed',
  germinacao: 'lab.tabs.germination',
  envelhecimento: 'lab.tabs.acceleratedAging',
};

const TEST_KIND_BY_TAB: Partial<Record<AnalysisTab, GerminationTestKind>> = {
  germinacao: 'GER',
  envelhecimento: 'EA',
};

const INSTALLATION_LIKE_TABS = new Set<AnalysisTab>(['instalacao', 'germinacao', 'envelhecimento']);

const NO_DETERMINATION_OPTIONS: { value: string; label: string }[] = [];

function SeedAnalysisListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: '/seeds/' });
  const { sync, isSyncing, lastSyncedAt } = useAnalysisSync();
  const search = Route.useSearch();
  const [searchInput, setSearchInput] = useState(search.search ?? '');

  const testKind = TEST_KIND_BY_TAB[search.tab];
  const isInstallationLike = INSTALLATION_LIKE_TABS.has(search.tab);
  const sort = resolveSort(search);
  const testDeterminations = useQuery({
    ...testDeterminationsQuery(testKind ?? 'GER'),
    enabled: !!testKind,
  });

  const analysisSortOptions: SortOption[] = [
    { value: ANALYSIS_COLUMN_KEYS.requester, label: t('lab.sort.requester') },
    { value: ANALYSIS_COLUMN_KEYS.sampleNumber, label: t('lab.sort.sampleNumber') },
    { value: ANALYSIS_COLUMN_KEYS.entryDate, label: t('lab.sort.entryDate') },
    { value: ANALYSIS_COLUMN_KEYS.species, label: t('lab.sort.species') },
    { value: ANALYSIS_COLUMN_KEYS.shipmentNumber, label: t('lab.sort.shipmentNumber') },
  ];

  const installationSortOptions: SortOption[] = [
    { value: ANALYSIS_COLUMN_KEYS.sampleNumber, label: t('lab.sort.sampleNumber') },
    { value: ANALYSIS_COLUMN_KEYS.species, label: t('lab.sort.species') },
    { value: ANALYSIS_COLUMN_KEYS.shipmentNumber, label: t('lab.sort.shipmentNumber') },
    { value: ANALYSIS_COLUMN_KEYS.determination, label: t('lab.sort.determination') },
    { value: ANALYSIS_COLUMN_KEYS.analysisDate, label: t('lab.sort.analysisDate') },
    { value: ANALYSIS_COLUMN_KEYS.status, label: t('lab.sort.status') },
  ];

  const updateSearch = (next: Partial<AnalysisListSearch>) => {
    void navigate({ search: { ...search, ...next } });
  };

  const applySearchTerm = useDebouncedCallback(
    (value: string) => updateSearch({ search: value || undefined, page: 1 }),
    { wait: SEARCH_DEBOUNCE_MS },
  );

  const changeSearchTerm = (value: string) => {
    setSearchInput(value);
    applySearchTerm(value);
  };

  const changePeriod = (period: PeriodValue) =>
    updateSearch({ from: period.from, to: period.to, page: 1 });

  const changeStatuses = (statuses: AnalysisStatusFilter[]) =>
    updateSearch({ status: serializeStatusFilters(statuses), page: 1 });

  const changeDeterminations = (determinations: string[]) =>
    updateSearch({ determinations: serializeDeterminationFilters(determinations), page: 1 });

  const changeDeadline = (deadline: DeadlineFilter | undefined) =>
    updateSearch({ deadline, page: 1 });

  const resetFilters = () => {
    setSearchInput('');
    updateSearch(clearedFilters());
  };

  const changeTab = (value: AnalysisTab) => {
    setSearchInput('');
    updateSearch({ ...clearedFilters(), tab: value });
  };

  const detailSearch = toDetailSearch(search);

  const openTetrazolium = (id: string) =>
    void navigate({ to: '/seeds/tetrazolium/$id', params: { id }, search: detailSearch });

  const openSandBed = (id: string) =>
    void navigate({ to: '/seeds/sand-bed/$id', params: { id }, search: detailSearch });

  const openInstallation = (items: InstallationListItem[]) =>
    void navigate({
      to: '/seeds/installation',
      search: { ...detailSearch, ids: items.map(({ id }) => id).join(',') },
    });

  const openGerminationItem = (item: InstallationListItem) => {
    const kind = toGerminationTestKind(item.determination);
    if (!kind) return;

    const needsInstallation = item.status === 'Pendente' && !item.installed && !item.isRetest;

    if (needsInstallation) {
      openInstallation([item]);
      return;
    }

    const needsAnalysisSetup =
      item.status !== 'Realizado' && (item.status === 'Pendente' || !item.analysisStarted);

    void navigate({
      to: GERMINATION_TEST_ROUTES[kind].path,
      params: { id: item.id },
      search: { ...detailSearch, setup: needsAnalysisSetup || undefined },
    });
  };

  const openInstallationItem = (item: InstallationListItem) => openInstallation([item]);

  const listFilters = {
    page: search.page,
    search: search.search,
    status: search.status,
    dataInicio: search.from,
    dataFim: search.to,
    orderBy: sort,
  };

  const installationFilters = {
    page: search.page,
    search: search.search,
    status: search.status,
    dataAnaliseInicio: search.from,
    dataAnaliseFim: search.to,
    orderBy: sort,
    determinations: parseDeterminationFilters(search.determinations),
    deadline: search.deadline && INSTALLATION_DEADLINES[search.deadline],
    statusScope: 'installation' as const,
  };

  const germinationDeterminationIds = parseDeterminationIds(search.determinations);

  const germinationFilters = {
    ...installationFilters,
    determinations: testKind ? [testKind] : [],
    determinationIds: germinationDeterminationIds,
    statusScope: 'analysis' as const,
  };

  const installationDeterminationOptions = INSTALLATION_DETERMINATIONS.map((determination) => ({
    value: determination,
    label: t(`installation.determinations.${determination}`),
  }));

  const germinationDeterminationOptions =
    testDeterminations.data?.map(({ id, label }) => ({ value: id, label })) ??
    NO_DETERMINATION_OPTIONS;

  const selectionScope = [
    search.tab,
    search.search,
    search.status,
    search.determinations,
    search.deadline,
    search.from,
    search.to,
    sort,
  ].join('|');

  const changePage = (page: number) => updateSearch({ page });

  const period = { from: search.from, to: search.to };
  const statuses = parseStatusFilters(search.status);

  const determinationFilter = testKind
    ? { options: germinationDeterminationOptions, selected: germinationDeterminationIds }
    : { options: installationDeterminationOptions, selected: installationFilters.determinations };

  const filters = isInstallationLike ? (
    <InstallationFilters<string>
      determinationOptions={determinationFilter.options}
      determinations={determinationFilter.selected}
      period={period}
      periodLabel={t(testKind ? 'installation.filters.period' : 'installation.filters.exitPeriod')}
      search={searchInput}
      statuses={statuses}
      onDeterminationsChange={changeDeterminations}
      onPeriodChange={changePeriod}
      onSearchChange={changeSearchTerm}
      onStatusesChange={changeStatuses}
    />
  ) : (
    <AnalysisFilters
      period={period}
      search={searchInput}
      statuses={statuses}
      onPeriodChange={changePeriod}
      onSearchChange={changeSearchTerm}
      onStatusesChange={changeStatuses}
    />
  );

  const samplesActions = isInstallationLike ? (
    <DeadlineToggle value={search.deadline} onChange={changeDeadline} />
  ) : undefined;

  const germinationList = (
    <InstallationList
      key={selectionScope}
      filters={germinationFilters}
      summaryKey="lab.summary"
      onInstall={openInstallation}
      onOpen={openGerminationItem}
      onPageChange={changePage}
    />
  );

  const listsByTab: Record<AnalysisTab, ReactNode> = {
    instalacao: (
      <InstallationList
        key={selectionScope}
        filters={installationFilters}
        onInstall={openInstallation}
        onOpen={openInstallationItem}
        onPageChange={changePage}
      />
    ),
    tetrazolio: (
      <TetrazoliumList filters={listFilters} onOpen={openTetrazolium} onPageChange={changePage} />
    ),
    'canteiro-areia': (
      <SandBedList filters={listFilters} onOpen={openSandBed} onPageChange={changePage} />
    ),
    germinacao: germinationList,
    envelhecimento: germinationList,
  };

  return (
    <PageLayout>
      <AppTopbar actions={<ProducerSelect />} lastSyncedAt={lastSyncedAt} title={t('lab.title')} />

      <AnalysisTabs value={search.tab} onChange={changeTab} />

      <AnalysisPanel
        filters={filters}
        hasActiveFilters={hasActiveFilters(search)}
        isSyncing={isSyncing}
        sortOptions={isInstallationLike ? installationSortOptions : analysisSortOptions}
        sortValue={sort}
        title={t(TAB_TITLE_KEYS[search.tab])}
        onResetFilters={resetFilters}
        onSortChange={(value) => updateSearch({ sort: value, page: 1 })}
        samplesActions={samplesActions}
        onSync={() => void sync()}
      >
        {listsByTab[search.tab]}
      </AnalysisPanel>
    </PageLayout>
  );
}

export const Route = createFileRoute('/seeds/')({
  validateSearch: analysisListSearchSchema,
  component: SeedAnalysisListPage,
});
